/**
 * Shared hook for editable encounter fields (appointment type, duration, CPT code, fee).
 * Used by both InlineAttendanceConfirm and BulkCompleteDialog.
 *
 * Fetches metadata on mount and provides fee-preview auto-recalculation.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch, buildApiUrl } from '../../../utils/apiClient';
import { getPatientFacingCategories } from '../../../utils/appointmentFormUtils';
import { logger } from '../../../utils/logger';

interface AppointmentTypeOption {
  id: string;
  name: string;
  category?: string;
  category_group?: string;
  default_duration_minutes?: number;
  allowed_durations?: number[];
}

interface CptCodeOption {
  id: string;
  code: string;
  description: string;
}

interface FeePreviewResult {
  base?: number | null;
  calculated: number | null;
  source: string;
  requires_manual_fee: boolean;
  warning?: string | null;
  admin_fee?: string | null;
  admin_fee_description?: string | null;
  discount?: { type: string; amount: number; reason?: string } | null;
}

interface EncounterEditState {
  appointmentTypeId: string | null;
  durationMinutes: number | null;
  cptCodeId: string | null;
  fee: number | null;
  feeOverridden: boolean;
}

const COMMON_DURATIONS = [15, 20, 25, 30, 45, 50, 53, 60, 75, 80, 90, 120];

export function useEncounterEditFields(opts: {
  initialAppointmentTypeId?: string | null;
  initialDurationMinutes?: number | null;
  initialCptCodeId?: string | null;
  initialFee?: number | null;
  patientId?: string | null;
  practitionerId?: string | null;
  caseId?: string | null;
  /** When false, metadata and fee preview are deferred until enabled. */
  enabled?: boolean;
}) {
  const {
    initialAppointmentTypeId,
    initialDurationMinutes,
    initialCptCodeId,
    initialFee,
    patientId,
    practitionerId,
    caseId,
    enabled = true,
  } = opts;

  // Metadata
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentTypeOption[]>([]);
  const [cptCodes, setCptCodes] = useState<CptCodeOption[]>([]);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const metadataLoadedRef = useRef(false);
  const metadataPractitionerIdRef = useRef<string | null | undefined>(undefined);
  const [reloadTick, setReloadTick] = useState(0);

  // Edit state
  const [state, setState] = useState<EncounterEditState>({
    appointmentTypeId: initialAppointmentTypeId ?? null,
    durationMinutes: initialDurationMinutes ?? null,
    cptCodeId: initialCptCodeId ?? null,
    fee: initialFee ?? null,
    feeOverridden: false,
  });

  // Fee preview
  const [feePreview, setFeePreview] = useState<FeePreviewResult | null>(null);
  const [feePreviewLoading, setFeePreviewLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Load appointment types + CPT codes when enabled or the provider changes
  useEffect(() => {
    if (!enabled) return;
    const effectivePractitionerId = practitionerId ?? null;
    if (
      metadataLoadedRef.current &&
      metadataPractitionerIdRef.current === effectivePractitionerId
    ) {
      return;
    }
    metadataLoadedRef.current = true;
    metadataPractitionerIdRef.current = effectivePractitionerId;

    const abortController = new AbortController();

    const load = async () => {
      setMetadataLoading(true);
      try {
        // Same list as the encounter modal: types the provider offers,
        // restricted to patient-facing categories (attendance is only
        // confirmed on patient appointments).
        const typesUrl = effectivePractitionerId
          ? `/api/calendar/appointments/metadata/appointment-types?practitioner_id=${effectivePractitionerId}`
          : '/api/calendar/appointments/metadata/appointment-types';
        const [typesRes, cptRes, patientFacing] = await Promise.all([
          apiFetch(buildApiUrl(typesUrl), {
            method: 'GET',
            credentials: 'include',
            signal: abortController.signal,
          }),
          apiFetch(
            buildApiUrl(
              `/api/calendar/appointments/metadata/cpt-codes${
                initialAppointmentTypeId ? `?appointment_type_id=${initialAppointmentTypeId}` : ''
              }`
            ),
            { method: 'GET', credentials: 'include', signal: abortController.signal }
          ),
          getPatientFacingCategories(),
        ]);
        if (typesRes.ok) {
          const types: AppointmentTypeOption[] = await typesRes.json();
          setAppointmentTypes(
            types.filter(
              (t) =>
                patientFacing.has(t.category_group || '') ||
                patientFacing.has(t.category || '')
            )
          );
        }
        if (cptRes.ok) setCptCodes(await cptRes.json());
      } catch (err) {
        // Ignore aborts from navigation/unmount; only log genuine failures.
        const isAbort =
          err instanceof DOMException && err.name === 'AbortError';
        const message = err instanceof Error ? err.message : String(err);
        if (!isAbort && !message.includes('Failed to fetch')) {
          logger.error('Failed to load encounter edit metadata:', err);
        }
      } finally {
        setMetadataLoading(false);
      }
    };
    load();

    return () => {
      abortController.abort();
    };
  }, [initialAppointmentTypeId, enabled, practitionerId, reloadTick]);

  // Reload CPT codes when appointment type changes
  const loadCptForType = useCallback(async (appointmentTypeId: string | null) => {
    try {
      const params = appointmentTypeId ? `?appointment_type_id=${appointmentTypeId}` : '';
      const res = await apiFetch(
        buildApiUrl(`/api/calendar/appointments/metadata/cpt-codes${params}`),
        { method: 'GET', credentials: 'include' }
      );
      if (res.ok) setCptCodes(await res.json());
    } catch {
      /* ignore */
    }
  }, []);

  // Fee preview auto-recalculation
  const fetchFeePreview = useCallback(
    async (aptTypeId: string | null, duration: number | null) => {
      if (!aptTypeId || !duration || !patientId || !caseId) {
        setFeePreview(null);
        return;
      }
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setFeePreviewLoading(true);
      try {
        const res = await apiFetch(buildApiUrl('/api/calendar/appointments/fee-preview'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            appointment_type_id: aptTypeId,
            duration_minutes: duration,
            primary_patient_id: patientId,
            provider_id: practitionerId || null,
            case_id: caseId,
          }),
          signal: ac.signal,
        });
        if (res.ok) {
          const data = await res.json();
          // API returns Decimal values as strings — coerce to numbers
          const parsed: FeePreviewResult = {
            ...data,
            calculated:
              data.calculated != null && data.calculated !== '' && !Number.isNaN(Number(data.calculated))
                ? Number(data.calculated)
                : null,
            admin_fee: data.admin_fee != null ? Number(data.admin_fee) : undefined,
          };
          setFeePreview(parsed);
          // Auto-update fee unless user manually overrode it
          setState((prev) => {
            if (prev.feeOverridden) return prev;
            return { ...prev, fee: parsed.calculated };
          });
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name !== 'AbortError') {
          logger.error('Fee preview error:', err);
        }
      } finally {
        if (!ac.signal.aborted) setFeePreviewLoading(false);
      }
    },
    [patientId, practitionerId, caseId]
  );

  // Setters that trigger cascading updates
  const setAppointmentTypeId = useCallback(
    (id: string | null) => {
      if (!enabled) return;
      setState((prev) => ({ ...prev, appointmentTypeId: id, feeOverridden: false }));
      loadCptForType(id);
      fetchFeePreview(id, state.durationMinutes);
    },
    [loadCptForType, fetchFeePreview, state.durationMinutes, enabled]
  );

  const setDurationMinutes = useCallback(
    (d: number | null) => {
      if (!enabled) return;
      setState((prev) => ({ ...prev, durationMinutes: d, feeOverridden: false }));
      fetchFeePreview(state.appointmentTypeId, d);
    },
    [fetchFeePreview, state.appointmentTypeId, enabled]
  );

  const setCptCodeId = useCallback((id: string | null) => {
    setState((prev) => ({ ...prev, cptCodeId: id }));
  }, []);

  const setFee = useCallback((f: number | null) => {
    setState((prev) => ({ ...prev, fee: f, feeOverridden: true }));
  }, []);

  // Available durations for current appointment type
  const selectedType = appointmentTypes.find((t) => t.id === state.appointmentTypeId);
  const availableDurations =
    selectedType?.allowed_durations && selectedType.allowed_durations.length > 0
      ? selectedType.allowed_durations
      : COMMON_DURATIONS;

  // Refetch metadata after offered-types changes
  const refreshMetadata = useCallback(() => {
    metadataLoadedRef.current = false;
    metadataPractitionerIdRef.current = undefined;
    setReloadTick((t) => t + 1);
  }, []);

  return {
    // Metadata
    appointmentTypes,
    cptCodes,
    availableDurations,
    metadataLoading,
    refreshMetadata,
    // State
    ...state,
    // Fee preview
    feePreview,
    feePreviewLoading,
    // Setters
    setAppointmentTypeId,
    setDurationMinutes,
    setCptCodeId,
    setFee,
  };
}
